import { useEffect, useState } from "react";
import { useParams, Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { ComplianceBadge } from "@/components/ComplianceBadge";
import { Download, FileSearch, ChevronLeft, ShieldAlert, ShoppingBag, ExternalLink, Check, Bell } from "lucide-react";

export default function ProductDetail() {
  const { sku } = useParams();
  const [searchParams] = useSearchParams();
  const requestedVariant = searchParams.get("variant");
  const [p, setP] = useState(null);
  const [loading, setLoading] = useState(true);
  const [variant, setVariant] = useState(null);
  const [qty, setQty] = useState(1);
  const { addToCart, cart } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    api.get(`/products/${sku}`).then((r) => {
      setP(r.data);
      const variants = r.data.variants || [];
      const matched = requestedVariant
        ? variants.find((v) => v.name.toLowerCase() === requestedVariant.toLowerCase())
        : null;
      setVariant(matched || variants.find((v) => v.in_stock) || variants[0] || null);
      setLoading(false);
    }).catch(() => { setP(null); setLoading(false); });
  }, [sku, requestedVariant]);

  // Refetch on BFCache restore / tab focus so admin price edits are always visible.
  useEffect(() => {
    const refetch = () => {
      api.get(`/products/${sku}`).then((r) => {
        setP(r.data);
        // Preserve the currently-selected variant if it still exists.
        setVariant((prev) => {
          const list = r.data.variants || [];
          if (!prev) return list.find((v) => v.in_stock) || list[0] || null;
          const same = list.find((v) => v.name === prev.name);
          return same || list.find((v) => v.in_stock) || list[0] || null;
        });
      }).catch(() => {});
    };
    const onPageShow = (e) => { if (e.persisted) refetch(); };
    const onFocus = () => refetch();
    window.addEventListener("pageshow", onPageShow);
    window.addEventListener("focus", onFocus);
    return () => {
      window.removeEventListener("pageshow", onPageShow);
      window.removeEventListener("focus", onFocus);
    };
  }, [sku]);

  if (loading) return <div className="max-w-screen-2xl mx-auto px-4 sm:px-6 py-16 font-mono text-xs uppercase tracking-[0.2em] text-[var(--ink-muted)]">Loading…</div>;
  if (!p) return <div className="max-w-screen-2xl mx-auto px-4 sm:px-6 py-16">Product not found.</div>;

  const activeCoa = (p.coas || []).find((c) => c.status === "active");
  const price = variant?.price ?? p.price_usd ?? 0;
  const original = variant?.original_price ?? p.original_price_usd ?? 0;
  const onSale = Boolean(original) && original > price;
  const discount = onSale ? Math.round(((original - price) / original) * 100) : 0;
  const variantOut = variant && !variant.in_stock;
  const allOut = !p.any_in_stock;

  const onAdd = () => {
    if (!p.allows_checkout) { toast.error("Not orderable."); return; }
    if (allOut || variantOut) { toast.error("Out of stock."); return; }
    const cap = Number(p.max_per_order || 0);
    if (cap > 0) {
      const alreadyInCart = (cart || [])
        .filter((i) => i.sku === p.sku)
        .reduce((s, i) => s + (Number(i.quantity) || 0), 0);
      if (alreadyInCart + qty > cap) {
        toast.error(`${p.product_name} is limited to ${cap} per order. You already have ${alreadyInCart} in your cart.`);
        return;
      }
    }
    const lineTotal = price * qty;
    const cartSubtotalAfter = (cart || []).reduce((s, i) => s + (Number(i.price) || 0) * (Number(i.quantity) || 0), 0) + lineTotal;
    addToCart({
      sku: p.sku, name: p.product_name, image: variant?.image_url || p.image_url,
      variant: variant?.name || p.pack_size, price, quantity: qty,
      max_per_order: Number(p.max_per_order || 0),
    });
    const remaining = 199 - cartSubtotalAfter;
    if (remaining > 0) {
      toast.success(`${p.product_name} (${variant?.name || p.pack_size}) added · Add $${remaining.toFixed(2)} to get free shipping`);
    } else {
      toast.success(`${p.product_name} (${variant?.name || p.pack_size}) added · You qualify for free shipping ✓`);
    }
  };

  const Spec = ({ label, value }) => (
    <div className="grid grid-cols-2 py-3 border-b border-[var(--line)]">
      <dt className="font-mono text-[10.5px] tracking-[0.18em] uppercase text-[var(--ink-muted)]">{label}</dt>
      <dd className="text-sm font-mono">{value || "—"}</dd>
    </div>
  );

  return (
    <div className="max-w-screen-2xl mx-auto px-4 sm:px-6 py-8 pb-mobile-ctas">
      <Link to="/shop" className="inline-flex items-center gap-1 text-xs font-mono uppercase tracking-wider text-[var(--ink-muted)] hover:text-[var(--ink)] mb-6" data-testid="back-link">
        <ChevronLeft className="h-3.5 w-3.5" /> Shop
      </Link>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
        <div className="lg:col-span-6">
          <div className="relative aspect-square bg-[var(--paper-2)] overflow-hidden rounded-md">
            {(variant?.image_url || p.image_url) ? <img key={variant?.image_url || p.image_url} src={variant?.image_url || p.image_url} alt={`${p.product_name}${variant?.name ? ` ${variant.name}` : ""}`} className="w-full h-full object-cover animate-in fade-in duration-300" data-testid="product-hero-image" /> : null}
            {onSale && (
              <div className="absolute top-4 left-4">
                <span className="sale-badge">−{discount}%</span>
              </div>
            )}
          </div>
          <div className="mt-4 bg-[var(--paper-2)] border border-[var(--line)] p-4 rounded-md flex items-start gap-3">
            <ShieldAlert className="h-5 w-5 text-[var(--terra)] shrink-0 mt-0.5" strokeWidth={1.6} />
            <p className="text-xs text-[var(--ink-soft)] leading-relaxed">
              All products sold by Nebula Aminos LLC are intended strictly for laboratory and in-vitro research purposes only. They are not intended for human or animal consumption, ingestion, injection, or any therapeutic, diagnostic, cosmetic, or clinical use. Products are not drugs, foods, dietary supplements, or medical devices, and have not been evaluated or approved by the FDA. By purchasing, the buyer certifies they are a qualified researcher or institution, are at least 18 years of age, and will handle all products in accordance with applicable laws and good laboratory practice. Misuse is strictly prohibited and is the sole responsibility of the purchaser.
            </p>
          </div>
        </div>

        <div className="lg:col-span-6">
          <div className="font-mono text-[10.5px] tracking-[0.18em] uppercase text-[var(--ink-muted)]" data-testid="product-meta">{p.sku} · {p.category}</div>
          <h1 className="h-display text-4xl md:text-5xl mt-2 leading-[1.04] tracking-tight" data-testid="product-name">{p.product_name}</h1>
          {p.manufacturer && (
            <div className="mt-3 inline-flex items-center gap-2 px-3 py-1 rounded-full border border-[var(--terra)]/40 bg-[var(--terra)]/10 text-[var(--terra)] font-mono text-[10.5px] tracking-[0.18em] uppercase" data-testid="product-manufacturer">
              <Check className="h-3 w-3" /> {p.manufacturer}® brand · authentic
            </div>
          )}
          {p.tagline && <p className="mt-3 text-[var(--ink-soft)]">{p.tagline}</p>}

          <div className="mt-6 flex items-baseline gap-3">
            <div className="font-display text-4xl font-semibold">${price.toFixed(2)}</div>
            {onSale && <div className="font-mono text-lg text-[var(--ink-muted)] line-through">${original.toFixed(2)}</div>}
            {onSale && <span className="sale-badge">−{discount}% Sale</span>}
          </div>

          {/* Variant selector */}
          {(p.variants || []).length > 0 && (
            <div className="mt-7">
              <div className="flex items-baseline justify-between mb-3">
                <div className="eyebrow">Size</div>
                {variant && (
                  <div className="font-mono text-[11px] tracking-[0.14em] uppercase" data-testid="variant-stock">
                    {!variant.in_stock || (variant.stock_qty || 0) <= 0 ? (
                      <span className="text-[var(--rust)]">Out of stock</span>
                    ) : variant.stock_qty <= 5 ? (
                      <span className="text-[var(--gold)]">Only {variant.stock_qty} left in stock</span>
                    ) : (
                      <span className="text-[var(--olive)]"><span className="inline-block h-1.5 w-1.5 rounded-full bg-[var(--olive)] mr-1.5 align-middle" />{variant.stock_qty} in stock</span>
                    )}
                  </div>
                )}
              </div>
              <div className="flex flex-wrap gap-2" data-testid="variant-selector">
                {p.variants.map((v) => (
                  <button
                    key={v.name}
                    onClick={() => setVariant(v)}
                    disabled={!v.in_stock}
                    className={`px-4 py-2.5 rounded-full border text-sm font-medium transition-colors ${variant?.name === v.name ? "border-[var(--ink)] bg-[var(--ink)] text-[var(--paper)]" : "border-[var(--line)] hover:border-[var(--ink)]"} ${!v.in_stock ? "opacity-40 cursor-not-allowed" : ""}`}
                    data-testid={`variant-${v.name.replace(/\s+/g, "-")}`}
                  >
                    {v.name}
                    {v.original_price && v.original_price > v.price && <span className="ml-2 text-[10px] font-mono text-[var(--terra)]">SALE</span>}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Qty + add */}
          <div className="mt-7 flex items-center gap-3">
            <div className="inline-flex items-center border border-[var(--line)] rounded-full overflow-hidden">
              <button onClick={() => setQty(Math.max(1, qty - 1))} className="px-3 py-2.5 hover:bg-[var(--paper-2)]" aria-label="Decrease">−</button>
              <input type="number" value={qty} onChange={(e) => {
                const next = Math.max(1, parseInt(e.target.value || "1"));
                const stockCap = variant?.stock_qty ?? 999;
                const orderCap = Number(p.max_per_order || 0) > 0 ? Number(p.max_per_order) : 999;
                setQty(Math.min(next, stockCap, orderCap));
              }} className="w-12 text-center font-mono outline-none" data-testid="product-qty" />
              <button
                onClick={() => {
                  const stockCap = variant?.stock_qty ?? 999;
                  const orderCap = Number(p.max_per_order || 0) > 0 ? Number(p.max_per_order) : 999;
                  setQty(Math.min(stockCap, orderCap, qty + 1));
                }}
                disabled={
                  (variant && variant.stock_qty && qty >= variant.stock_qty) ||
                  (Number(p.max_per_order || 0) > 0 && qty >= Number(p.max_per_order))
                }
                className="px-3 py-2.5 hover:bg-[var(--paper-2)] disabled:opacity-40 disabled:cursor-not-allowed"
                aria-label="Increase"
              >+</button>
            </div>
            <button onClick={onAdd} disabled={!p.allows_checkout || allOut || variantOut} className="btn-primary flex-1 justify-center disabled:bg-[var(--ink-muted)]" data-testid="add-to-bag">
              <ShoppingBag className="h-4 w-4" /> {!p.allows_checkout ? "Unavailable" : (allOut || variantOut) ? "Out of stock" : "Add to bag"}
            </button>
          </div>
          {(allOut || variantOut) && p.allows_checkout && (
            <NotifyBackForm sku={p.sku} variant={variant?.name} />
          )}
          {Number(p.max_per_order || 0) > 0 && (
            <div className="mt-2 font-mono text-[10.5px] tracking-[0.16em] uppercase text-[var(--ink-muted)]" data-testid="max-per-order-note">
              Limit {p.max_per_order} per order
            </div>
          )}

          {/* Trust row */}
          <div className="mt-7 grid grid-cols-2 sm:grid-cols-3 gap-4 text-xs">
            <Trust k="Per-batch COA" v={variant?.batch_number ? `Batch ${variant.batch_number}` : "COA pending · this lot"} />
            <Trust k="Tested by" v={p.coa_provider || "Koveralabs"} />
            <Trust k="Ships in" v="1–2 business days" />
          </div>

          {/* Description */}
          {p.description && (
            <div className="mt-10">
              <div className="eyebrow mb-2">About this compound</div>
              <p className="text-[15px] leading-relaxed text-[var(--ink-soft)]">{p.description}</p>
            </div>
          )}

          {/* Spec sheet */}
          <div className="mt-10">
            <div className="eyebrow mb-2">Specification</div>
            <dl className="border-t border-[var(--line)]">
              <Spec label="SKU" value={p.sku} />
              <Spec label="CAS Number" value={p.cas_number} />
              <Spec label="Molecular Formula" value={p.molecular_formula} />
              <Spec label="Molecular Weight" value={p.molecular_weight} />
              {p.sequence && <Spec label="Sequence" value={p.sequence} />}
              <Spec label="Form" value={p.form || p.appearance} />
              <Spec label="Cap Color" value={p.cap_color} />
              <Spec label="Crimp Color" value={p.crimp_color} />
              <Spec label="Storage" value={p.storage_conditions} />
              <Spec label="Testing Method" value={p.testing_method} />

              {/* Per-variant COA-derived fields */}
              {variant && (
                <>
                  <div className="mt-4 pt-3 border-t border-[var(--line)]">
                    <div className="eyebrow flex items-center gap-2">
                      Test results · {variant.name}
                      {variant.batch_number ? (
                        <span className="font-mono text-[10px] tracking-[0.16em] uppercase text-[var(--olive)]">COA verified</span>
                      ) : (
                        <span className="font-mono text-[10px] tracking-[0.16em] uppercase text-[var(--ink-muted)]">COA pending</span>
                      )}
                    </div>
                  </div>
                  <Spec label="Labeled Qty" value={variant.labeled_qty || variant.name} />
                  {variant.batch_number && <Spec label="Batch #" value={variant.batch_number} />}
                  {variant.purity_percent && <Spec label="Purity %" value={`${variant.purity_percent}%`} />}
                  {variant.net_content && <Spec label="Net Content (tested)" value={variant.net_content} />}
                  {variant.test_date && <Spec label="Test Date" value={variant.test_date} />}
                  {!variant.batch_number && (
                    <div className="text-xs text-[var(--ink-muted)] font-mono py-3">
                      No published COA for this lot yet. Each dose ships with its own batch-specific lab report.
                    </div>
                  )}
                </>
              )}
            </dl>
          </div>

          {/* COA records */}
          <div className="mt-10 border border-[var(--line)] rounded-md overflow-hidden">
            <div className="px-4 py-3 border-b border-[var(--line)] bg-[var(--paper-2)] flex items-center justify-between">
              <div className="eyebrow">Lab Reports</div>
              <Link to={`/verify?sku=${p.sku}`} className="text-xs font-mono uppercase tracking-wider text-[var(--ink)] hover:text-[var(--terra)] inline-flex items-center gap-1" data-testid="public-coa-search">
                <FileSearch className="h-3.5 w-3.5" /> Public batch search
              </Link>
            </div>
            {(() => {
              const variantCoas = variant?.batch_number
                ? (p.coas || []).filter((c) => c.batch_number === variant.batch_number)
                : [];
              if (variantCoas.length === 0) {
                return (
                  <div className="px-4 py-6 text-center text-[var(--ink-muted)] font-mono text-xs uppercase tracking-wider" data-testid="lab-reports-empty">
                    No COA on file for {variant?.name || "this lot"} yet · check back soon
                  </div>
                );
              }
              // Renders the per-COA action link (Koveralabs verify, PDF, or
              // Koveralabs generic fallback). Plain function returning JSX —
              // NOT a nested component — so React doesn't remount on each parent render.
              const renderAction = (c) => (
                c.external_verify_url ? (
                  <a href={c.external_verify_url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-[var(--terra)] hover:text-[var(--terra-2)] font-mono text-xs" data-testid={`verify-link-${c.batch_number}`}>
                    <ExternalLink className="h-3.5 w-3.5" /> Verify on Koveralabs
                  </a>
                ) : c.coa_pdf_url ? (
                  <a href={`${process.env.REACT_APP_BACKEND_URL}${c.coa_pdf_url}`} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-[var(--ink)] hover:text-[var(--terra)] font-mono text-xs" data-testid={`coa-pdf-${c.batch_number}`}>
                    <Download className="h-3.5 w-3.5" /> Download PDF
                  </a>
                ) : c.testing_lab === "Koveralabs" ? (
                  <a href="https://koveralabs.com" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-[var(--ink)] hover:text-[var(--terra)] font-mono text-xs">
                    <ExternalLink className="h-3.5 w-3.5" /> Koveralabs
                  </a>
                ) : (
                  <span className="font-mono text-xs text-[var(--ink-muted)]">—</span>
                )
              );
              return (
                <>
                  {/* Mobile: stacked card layout. Full-width verify button so
                      it can never be scrolled off-screen. */}
                  <div className="md:hidden divide-y divide-[var(--line)]" data-testid="lab-reports-mobile">
                    {variantCoas.map((c) => (
                      <div key={c.batch_number} className="p-4" data-testid={`coa-card-${c.batch_number}`}>
                        <div className="flex items-start justify-between gap-3">
                          <div>
                            <div className="eyebrow">Batch</div>
                            <div className="font-mono text-sm mt-0.5">{c.batch_number}</div>
                          </div>
                          <ComplianceBadge kind="coa" value={c.status} />
                        </div>
                        <div className="mt-3 grid grid-cols-3 gap-3 text-[13px]">
                          <div>
                            <div className="eyebrow">Purity</div>
                            <div className="font-mono mt-0.5">{c.purity_percent}%</div>
                          </div>
                          <div>
                            <div className="eyebrow">Date</div>
                            <div className="font-mono mt-0.5">{c.test_date}</div>
                          </div>
                          <div>
                            <div className="eyebrow">Method</div>
                            <div className="font-mono mt-0.5 break-words">{(c.testing_methods || []).join(", ")}</div>
                          </div>
                        </div>
                        <div className="mt-4 flex justify-center border border-[var(--line)] rounded-md py-2.5 bg-[var(--paper-2)]">
                          {renderAction(c)}
                        </div>
                      </div>
                    ))}
                  </div>
                  {/* Desktop: original table layout. */}
                  <table className="w-full data-table hidden md:table">
                    <thead>
                      <tr>
                        <th className="text-left px-4 py-2">Batch</th>
                        <th className="text-left px-4 py-2">Purity</th>
                        <th className="text-left px-4 py-2">Method</th>
                        <th className="text-left px-4 py-2">Date</th>
                        <th className="text-left px-4 py-2">Status</th>
                        <th className="text-right px-4 py-2">PDF</th>
                      </tr>
                    </thead>
                    <tbody>
                      {variantCoas.map((c) => (
                        <tr key={c.batch_number} data-testid={`coa-row-${c.batch_number}`}>
                          <td className="px-4 py-3 font-mono">{c.batch_number}</td>
                          <td className="px-4 py-3 font-mono">{c.purity_percent}%</td>
                          <td className="px-4 py-3 font-mono">{(c.testing_methods || []).join(", ")}</td>
                          <td className="px-4 py-3 font-mono">{c.test_date}</td>
                          <td className="px-4 py-3"><ComplianceBadge kind="coa" value={c.status} /></td>
                          <td className="px-4 py-3 text-right">{renderAction(c)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </>
              );
            })()}
          </div>
        </div>
      </div>

      {/*
       * Mobile-only sticky "Add to bag" bar.
       * Sits above the fixed bottom tab bar (offset ~64 px + safe-area) so
       * buyers on 375-430 px screens always have the primary purchase action
       * one thumb-tap away, even after scrolling to the COA section.
       * Hidden on lg+ where the desktop right-column CTA is always visible.
       */}
      <div
        className="lg:hidden fixed inset-x-0 z-30 border-t border-[var(--line)] bg-[var(--paper)]/95 backdrop-blur-lg"
        style={{
          bottom: "calc(64px + env(safe-area-inset-bottom))",
          paddingBottom: "8px",
          paddingTop: "8px",
        }}
        data-testid="mobile-buy-bar"
      >
        <div className="mx-auto max-w-screen-sm px-3 flex items-center gap-3">
          <div className="flex-shrink-0">
            <div className="font-mono text-[10px] uppercase tracking-[0.14em] text-[var(--ink-muted)] leading-none">
              {variant?.name || p.pack_size}
            </div>
            <div className="font-display text-lg font-semibold leading-tight">${price.toFixed(2)}</div>
          </div>
          <button
            onClick={onAdd}
            disabled={!p.allows_checkout || allOut || variantOut}
            className="btn-primary flex-1 justify-center disabled:bg-[var(--ink-muted)] h-12"
            data-testid="mobile-add-to-bag"
          >
            <ShoppingBag className="h-4 w-4" />
            {!p.allows_checkout ? "Unavailable" : (allOut || variantOut) ? "Out of stock" : "Add to bag"}
          </button>
        </div>
      </div>
    </div>
  );
}

const Trust = ({ k, v }) => (
  <div className="border border-[var(--line)] bg-[var(--paper-2)] rounded-md p-3">
    <div className="font-mono text-[9.5px] uppercase tracking-[0.18em] text-[var(--ink-muted)]">{k}</div>
    <div className="text-[12.5px] mt-0.5 font-medium">{v}</div>
  </div>
);

function NotifyBackForm({ sku, variant }) {
  const { user } = useAuth();
  const isAuthed = !!user?.email;
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (user?.email) setEmail(user.email);
  }, [user]);

  // Reset the confirmation state whenever the buyer flips to a different variant
  useEffect(() => { setDone(false); }, [variant]);

  const submitWithEmail = async (targetEmail) => {
    const clean = (targetEmail || "").trim();
    if (!clean) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/products/${encodeURIComponent(sku)}/notify-back`, {
        email: clean,
        variant: variant || undefined,
      });
      setDone(true);
      toast.success(data.already_subscribed
        ? "You're already on the list — we'll email you the moment it's back."
        : "You're on the list. We'll email you the moment it's back.");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not sign you up — try again in a moment.");
    } finally {
      setBusy(false);
    }
  };

  const submitForm = (e) => { e.preventDefault(); submitWithEmail(email); };
  const submitOneClick = () => submitWithEmail(user?.email);

  if (done) {
    return (
      <div className="mt-4 border border-[var(--terra)]/40 bg-[var(--terra)]/10 rounded-md p-4 flex items-start gap-3" data-testid="notify-back-done">
        <Check className="h-4 w-4 text-[var(--terra)] mt-0.5" />
        <div>
          <div className="text-sm font-medium">You&apos;re on the list.</div>
          <div className="text-xs text-[var(--ink-soft)] mt-1">We&apos;ll email <span className="font-mono">{isAuthed ? user.email : email}</span> the moment {variant || "this size"} is back in stock — usually within a week.</div>
        </div>
      </div>
    );
  }

  // Signed-in buyers: no email typing required — single-click enrollment.
  if (isAuthed) {
    return (
      <div className="mt-4 border border-[var(--line)] bg-[var(--paper-2)] rounded-md p-4" data-testid="notify-back-form">
        <div className="flex items-center gap-2 text-xs font-mono uppercase tracking-[0.16em] text-[var(--ink-soft)]">
          <Bell className="h-3.5 w-3.5 text-[var(--terra)]" /> Notify me when back
        </div>
        <p className="mt-1 text-xs text-[var(--ink-muted)]">
          We&apos;ll email <span className="font-mono text-[var(--ink)]">{user.email}</span> — one message, no newsletter.
        </p>
        <button
          type="button"
          onClick={submitOneClick}
          disabled={busy}
          className="btn-primary w-full mt-3 justify-center disabled:bg-[var(--ink-muted)]"
          data-testid="notify-back-submit"
        >
          <Bell className="h-4 w-4" /> {busy ? "Signing you up…" : "Notify me when back"}
        </button>
      </div>
    );
  }

  // Guests: keep the email input form.
  return (
    <form onSubmit={submitForm} className="mt-4 border border-[var(--line)] bg-[var(--paper-2)] rounded-md p-4" data-testid="notify-back-form">
      <div className="flex items-center gap-2 text-xs font-mono uppercase tracking-[0.16em] text-[var(--ink-soft)]">
        <Bell className="h-3.5 w-3.5 text-[var(--terra)]" /> Notify me when back
      </div>
      <p className="mt-1 text-xs text-[var(--ink-muted)]">One-time email. No newsletter, no spam.</p>
      <div className="mt-3 flex flex-col sm:flex-row gap-2">
        <input
          type="email"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="you@lab.com"
          className="flex-1 px-3 py-2.5 border border-[var(--line)] bg-[var(--paper)] rounded-md text-sm outline-none focus:border-[var(--terra)]"
          data-testid="notify-back-email"
        />
        <button
          type="submit"
          disabled={busy}
          className="btn-primary justify-center disabled:bg-[var(--ink-muted)]"
          data-testid="notify-back-submit"
        >
          {busy ? "Signing you up…" : "Notify me"}
        </button>
      </div>
    </form>
  );
}

